import { axiosClient as axios } from './axiosClient';

import { api } from '@config/apiEndpoints';
import {
  failureResult,
  ResultViewModel,
  successResult,
} from '@models/common/ResultViewModel';
import { CustomerSelectOptionResultViewModel } from '@models/Customer';
import {
  RecurringInvoiceResultViewModel,
  RecurringInvoiceTableResultViewModel,
  RecurringInvoiceViewModel,
} from '@models/RecurringInvoice';
import { RecurringInvoiceDetailListResultViewModel } from '@models/RecurringInvoiceDetail';
import { handleAxiosError } from '@utils/axiosErrorHandler';
import { getDataUrl } from '@utils/dataUrls';

export const RecurringInvoiceService = {
  create: async function (
    recurringInvoice: RecurringInvoiceViewModel
  ): Promise<RecurringInvoiceResultViewModel | ResultViewModel> {
    const createUrl = `${api.BASE_URL}${api.RECURRING_INVOICES_ENDPOINT}`;

    try {
      const response = await axios.post(createUrl, recurringInvoice);
      return response.data as RecurringInvoiceResultViewModel;
    } catch (error) {
      return handleAxiosError(error);
    }
  },

  getList: async function (
    page?: number,
    pageSize?: number,
    searchFields?: string,
    search?: string,
    filters?: string,
    sortBy?: string,
    sortDirection?: string
  ): Promise<RecurringInvoiceTableResultViewModel | ResultViewModel> {
    let dataUrl = getDataUrl(
      api.BASE_URL,
      api.RECURRING_INVOICES_ENDPOINT,
      page,
      pageSize,
      searchFields,
      search,
      sortBy,
      sortDirection
    );

    if (filters) {
      dataUrl = dataUrl + filters;
    }

    try {
      const response = await axios.get(dataUrl);
      return response.data as RecurringInvoiceTableResultViewModel;
    } catch (error) {
      return handleAxiosError(error);
    }
  },

  getById: async function (
    id: number
  ): Promise<RecurringInvoiceResultViewModel | ResultViewModel> {
    const dataUrl = `${api.BASE_URL}${api.RECURRING_INVOICES_ENDPOINT}/${id}`;

    try {
      const response = await axios.get(dataUrl);
      return response.data as RecurringInvoiceResultViewModel;
    } catch (error) {
      return handleAxiosError(error);
    }
  },

  delete: async function (id: number): Promise<ResultViewModel> {
    const deleteURL = `${api.BASE_URL}${api.RECURRING_INVOICES_ENDPOINT}/${id}`;

    try {
      const response = await axios.delete(deleteURL);
      if (response.status === 204) {
        return successResult;
      }

      return failureResult;
    } catch (error) {
      return handleAxiosError(error);
    }
  },

  update: async function (
    id: number,
    estimates: RecurringInvoiceViewModel
  ): Promise<ResultViewModel> {
    const updateUrl = `${api.BASE_URL}${api.RECURRING_INVOICES_ENDPOINT}/${id}`;

    try {
      const response = await axios.put(updateUrl, estimates);
      if (response.status === 204) {
        return successResult;
      }

      return failureResult;
    } catch (error) {
      return handleAxiosError(error);
    }
  },
  duplicate: async function (id: number): Promise<ResultViewModel> {
    const duplicateUrl = `${api.BASE_URL}${api.RECURRING_INVOICES_ENDPOINT}/${id}/duplicate`;

    try {
      const response = await axios.post(duplicateUrl);
      if (response.status === 200) {
        return successResult;
      }

      return failureResult;
    } catch (error) {
      return handleAxiosError(error);
    }
  },
  activate: async function (id: number): Promise<ResultViewModel> {
    const activateUrl = `${api.BASE_URL}${api.RECURRING_INVOICES_ENDPOINT}/${id}/activate`;

    try {
      const response = await axios.post(activateUrl);
      if (response.status === 200) {
        return successResult;
      }

      return failureResult;
    } catch (error) {
      return handleAxiosError(error);
    }
  },
  suspend: async function (id: number): Promise<ResultViewModel> {
    const suspendUrl = `${api.BASE_URL}${api.RECURRING_INVOICES_ENDPOINT}/${id}/suspend`;

    try {
      const response = await axios.post(suspendUrl);
      if (response.status === 200) {
        return successResult;
      }

      return failureResult;
    } catch (error) {
      return handleAxiosError(error);
    }
  },

  end: async function (id: number): Promise<ResultViewModel> {
    const endUrl = `${api.BASE_URL}${api.RECURRING_INVOICES_ENDPOINT}/${id}/end`;

    try {
      const response = await axios.post(endUrl);
      if (response.status === 200) {
        return successResult;
      }

      return failureResult;
    } catch (error) {
      return handleAxiosError(error);
    }
  },
  getRecurringInvoiceDetails: async function (
    id: number
  ): Promise<RecurringInvoiceDetailListResultViewModel | ResultViewModel> {
    const dataUrl = `${api.BASE_URL}${api.RECURRING_INVOICES_ENDPOINT}/${id}/details`;

    try {
      const response = await axios.get(dataUrl);
      return response.data as RecurringInvoiceDetailListResultViewModel;
    } catch (error) {
      return handleAxiosError(error);
    }
  },
  getCustomerFilterSelectOptions: async function (): Promise<
    CustomerSelectOptionResultViewModel | ResultViewModel
  > {
    const dataUrl = `${api.BASE_URL}${api.RECURRING_INVOICES_ENDPOINT}/customer-filter-select-options`;

    try {
      const response = await axios.get(dataUrl);
      return response.data as CustomerSelectOptionResultViewModel;
    } catch (error) {
      return handleAxiosError(error);
    }
  },
};
